;(function (angular, _) {
  'use strict'
  angular.module('kilo.common.services')
    .service('PennylaneEmissionTrackerService', PennylaneEmissionTrackerService)

  PennylaneEmissionTrackerService.$inject = [
    // Angular
    '$interval',
    '$q',
    // Libs
    // Managers
    // Services
    'DisplayService',
    'PennylaneService',
    'SnackService'
    // Const
    // Resolved
  ]

  /**
   * Suivi du résultat des émissions de factures vers Pennylane.
   *
   * `POST /invoices/emit` est asynchrone côté backend : il répond
   * `{ accepted, items: [{ kiloInvoiceId, monitoringId }] }` immédiatement puis
   * traite le lot en arrière-plan (1 doc Monitoring par facture). Sans suivi,
   * un échec réel (TVA invalide, adresse incomplète, 422 Pennylane…) est
   * invisible pour l'utilisateur. Ce service polle `GET /monitoring/:id`
   * jusqu'à résolution puis restitue : snack de succès, snack info si des
   * factures ont été ignorées, dialog récapitulatif si au moins un échec.
   *
   * Singleton : il survit aux `$state.reload()` des écrans appelants (seul le
   * contrôleur est rechargé) → le résultat s'affiche même après le reload.
   * Chaque appel à `track()` gère son propre état/$interval → plusieurs suivis
   * simultanés possibles (deux envois successifs depuis deux écrans).
   * Aucun état persistant (v1 session courante uniquement).
   */
  function PennylaneEmissionTrackerService (// Angular
    $interval,
    $q,
    // Libs
    // Managers
    // Services
    DisplayService,
    PennylaneService,
    SnackService
    // Const
    // Resolved
  ) {
    const self = this

    // Période de polling des Monitoring.
    const POLL_INTERVAL_MS = 5000
    // ⚠️ TIMEOUT OBLIGATOIRE (décision de review backend) : un Monitoring peut
    // rester `pending` à jamais (restart du pod backend en plein lot) → jamais
    // de polling infini. Délai : 10 min + 30 s par facture au-delà de la 1re,
    // plafonné à 30 min.
    const BASE_TIMEOUT_MS = 10 * 60 * 1000
    const PER_ITEM_TIMEOUT_MS = 30 * 1000
    const MAX_TIMEOUT_MS = 30 * 60 * 1000

    _.extend(self, {
      // Public Attributes
      // Public Method
      track
    })

    /**
     * Lance le suivi d'un lot d'émissions.
     * @param items `[{ kiloInvoiceId, monitoringId, label }]` — `label` =
     *   numéro/libellé de la facture pour l'affichage (fallback kiloInvoiceId).
     *   Les entrées sans `monitoringId` sont ignorées (tolérance backend).
     */
    function track (items) {
      const tracked = _.filter(items || [], (item) => item && item.monitoringId)
        .map((item) => ({
          monitoringId: item.monitoringId,
          label: String(item.label || item.kiloInvoiceId || item.monitoringId),
          done: false,
          status: null,
          info: null
        }))
      if (!tracked.length) return

      const timeoutMs = Math.min(
        BASE_TIMEOUT_MS + PER_ITEM_TIMEOUT_MS * (tracked.length - 1),
        MAX_TIMEOUT_MS
      )
      const startedAt = Date.now()
      // Garde anti-chevauchement : si un tick est encore en vol (réseau lent),
      // les ticks suivants sont sautés plutôt qu'empilés.
      let polling = false
      const intervalPromise = $interval(tick, POLL_INTERVAL_MS)

      function tick () {
        if (polling) return
        polling = true
        const pending = tracked.filter((item) => !item.done)
        // pollOne ne rejette jamais → $q.all résout toujours.
        $q.all(pending.map(pollOne)).then(() => {
          polling = false
          const stillPending = tracked.filter((item) => !item.done)
          if (!stillPending.length) {
            $interval.cancel(intervalPromise)
            report(tracked, 0)
          } else if (Date.now() - startedAt >= timeoutMs) {
            $interval.cancel(intervalPromise)
            report(tracked, stillPending.length)
          }
        })
      }

      /**
       * Relève le Monitoring d'un item non terminé. Une erreur HTTP/réseau est
       * transitoire : l'item reste non terminé et sera réessayé au tick
       * suivant (jamais d'échec du tracking sur une erreur de GET).
       * Limite assumée (v1) : après un logout ou un changement de company
       * pendant le suivi, les GET répondent 404/-1 (ignorés ici) → le tracking
       * court jusqu'au timeout et le snack final peut arriver hors contexte.
       */
      function pollOne (item) {
        return PennylaneService.getMonitoring(item.monitoringId).then((monitoring) => {
          if (!monitoring || !monitoring.status) return
          item.status = monitoring.status
          item.info = monitoring.info || null
          // `postponed` = retry planifié côté backend → toujours en cours.
          if (monitoring.status !== 'pending' && monitoring.status !== 'postponed') {
            item.done = true
          }
        }).catch(angular.noop)
      }
    }

    /**
     * Restitution finale d'un suivi (tous terminés, ou timeout atteint).
     * @param tracked items du suivi (résolus ou non)
     * @param unresolvedCount nombre d'items toujours en cours au timeout
     */
    function report (tracked, unresolvedCount) {
      if (unresolvedCount > 0) {
        // Timeout : snack neutre — les factures déjà résolues sont restituées
        // normalement ci-dessous.
        SnackService.snack({
          text: `Suivi Pennylane interrompu : ${unresolvedCount} envoi(s) toujours en cours`,
          delay: 10000
        })
      }
      const resolved = tracked.filter((item) => item.done)
      if (!resolved.length) return

      const skipped = resolved.filter((item) =>
        item.status === 'completed' && item.info && item.info.skipped)
      const succeeded = resolved.filter((item) =>
        item.status === 'completed' && !(item.info && item.info.skipped))
      // « Non abouti » = échec (`failed`) OU statut terminé inconnu (∉
      // completed/failed, ex. nouveau statut backend) : dans le doute, on
      // MONTRE (l'objet du suivi est la visibilité — un item ne doit jamais
      // disparaître des comptes).
      const problems = resolved
        .filter((item) => item.status !== 'completed')
        .map((item) => ({
          label: item.label,
          reason: item.status === 'failed'
            ? failureReason(item.info)
            : `statut inattendu : ${item.status}`
        }))

      if (problems.length) {
        return reportFailures(problems, succeeded.length, skipped.length)
      }
      if (skipped.length) {
        // Skips sans échec : snack info avec le détail envoyées/ignorées.
        const parts = []
        if (succeeded.length) parts.push(`${succeeded.length} facture(s) envoyée(s) sur Pennylane`)
        parts.push(`${skipped.length} déjà envoyée(s)/ignorée(s)`)
        SnackService.snack({
          text: parts.join(', '),
          delay: 10000
        })
        return
      }
      // Tous réussis.
      SnackService.snack({
        text: succeeded.length === 1
          ? `Facture ${succeeded[0].label} envoyée sur Pennylane`
          : `${succeeded.length} facture(s) envoyée(s) sur Pennylane`,
        delay: 10000
      })
    }

    /**
     * Dialog récapitulatif quand au moins une émission n'a pas abouti : une
     * ligne par problème « <label> : <motif> » + compte des réussites/skips.
     * On réutilise DisplayService.displayConfirm (seul dialog générique du
     * repo, template confirm.html en ng-bind-html) — les deux boutons ferment.
     * Limite assumée (v1) : les modals sont fermées par toute transition
     * d'état ($uibModalStack.dismissAll dans DisplayService) → une navigation
     * pendant l'affichage ferme le récapitulatif ; l'échec reste consultable
     * sur la fiche facture (suivi d'émission, U5).
     * @param problems `[{ label, reason }]` (motifs déjà résolus par l'appelant)
     */
    function reportFailures (problems, successCount, skipCount) {
      const lines = problems.map((item) =>
        `<li>${escapeHtml(item.label)} : ${escapeHtml(item.reason)}</li>`)
      let message = `${problems.length} envoi(s) en échec :<ul>${lines.join('')}</ul>`
      const extras = []
      if (successCount > 0) extras.push(`${successCount} facture(s) envoyée(s)`)
      if (skipCount > 0) extras.push(`${skipCount} déjà envoyée(s)/ignorée(s)`)
      if (extras.length) {
        message += `<br>${extras.join(', ')}.`
      }
      // .result rejette quand l'utilisateur ferme via ANNULER/Échap : ce n'est
      // pas une erreur ici (simple récapitulatif) → catch silencieux.
      DisplayService.displayConfirm('Envoi sur Pennylane : échec(s)', message)
        .catch(angular.noop)
    }

    /**
     * Motif d'échec lisible depuis `Monitoring.info` (borné à 300 côté
     * backend ; formes connues : `{ message }`, `{ step, reason,
     * upstreamStatus, upstreamMessage }`, `{ step, pennylaneInvoiceId, message }`).
     */
    function failureReason (info) {
      if (!info) return 'Erreur inconnue'
      return info.upstreamMessage || info.message || info.reason || 'Erreur inconnue'
    }

    /**
     * Échappement HTML : le template du dialog interprète le message en
     * ng-bind-html (ngSanitize) — labels et motifs backend sont insérés en
     * texte littéral, jamais en balisage.
     */
    function escapeHtml (value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
    }
  }
})(window.angular, window._)
